import React, { useState, useEffect, useRef } from 'react'
import { Form, Input, InputNumber, Switch, Button, Select, Slider, Divider, Tabs, Alert, App } from 'antd'
import type { TabsProps } from 'antd'
import { AppCard } from '@/shared/components/ui'
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons'
import { useI18n } from '@/shared/i18n'
import type { SupportedLanguage } from '@/shared/i18n/constants'
import { SUPPORTED_LANGUAGES, LANGUAGE_DISPLAY_NAMES } from '@/shared/i18n/constants'
import { useSettingsStore, defaultSettings } from './SystemSettings.store'
import {
  useCompanySettings,
  useUpdateCompanySettings,
  useKnowledgeBaseHasDocuments,
} from '@/shared/api/companyApi'
import { useAuthStore } from '@/stores/authStore'
import SensitiveFieldInput from './components/SensitiveFieldInput'
import TenantExportImport from './components/TenantExportImport'
import WhatsAppChannelConfig from './components/WhatsAppChannelConfig'
import './SystemSettings.scss'

const { Option } = Select

interface SystemSettings {
  general: {
    language: SupportedLanguage
    timezone: string
  }
  ai: {
    model: string
    temperature: number
    maxTokens: number
    enableContextWindow: boolean
    contextWindow?: number
    maxResponseChars?: number
    enableStreaming: boolean
    apiKey?: string
    embeddingModel?: string  // "local" | "dashscope"
    embeddingApiKey?: string  // 仅dashscope模型需要
    embeddingEndpointUrl?: string  // 仅local模型需要，Ollama endpoint URL
  }
  notifications: {
    emailEnabled: boolean
    emailRecipients: string[]
    slackEnabled: boolean
    slackWebhook: string
    enableErrorAlerts: boolean
  }
  integrations: {
    enableWebhook: boolean
    webhookUrl: string
    enableAPI: boolean
    apiRateLimit: number
    allowedOrigins: string[]
  }
  style?: {
    persona?: string;
    base_tone?: {
      formality_level?: string;
      politeness_level?: string;
      enthusiasm_level?: string;
    };
    emoji_policy?: string;
    response_preferences?: {
      use_emphasis?: boolean;
      line_breaks?: boolean;
    };
  }
}

const SystemSettings: React.FC = () => {
  const { t, currentLanguage, changeLanguage } = useI18n('modules')
  console.log('SystemSettings i18n:', { currentLanguage, changeLanguage })
  const { user, refreshUserInfo } = useAuthStore()  // 添加 refreshUserInfo
  const { message } = App.useApp() // 使用 App hooks 获取 message
  const {
    settings,
    isLoading,
    isSaving,
    error,
    fetchSettings,
    updateSettings,
    saveSettings,
    resetSettings
  } = useSettingsStore()
  
  // 租户配置相关
  const { data: companySettingsData, isLoading: isLoadingCompanySettings } = useCompanySettings()
  const updateCompanySettings = useUpdateCompanySettings()
  // 知识库文档状态
  const { data: knowledgeBaseStatus, isLoading: isLoadingKnowledgeBaseStatus } = useKnowledgeBaseHasDocuments()
  
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('general')
  const [tenantLanguage, setTenantLanguage] = useState<SupportedLanguage>('zh-CN')
  // 跟踪租户设置是否已应用，防止重复设置表单值
  const hasAppliedTenantSettingsRef = useRef(false)
  const previousCompanySettingsDataRef = useRef<any>(null)
  // 监听上下文窗口开关和嵌入模型选择变化
  const enableContextWindow = Form.useWatch(['ai', 'enableContextWindow'], form)
  const embeddingModel = Form.useWatch(['ai', 'embeddingModel'], form)

  // 响应式监听敏感字段状态
  const hasApiKey = Form.useWatch(['ai', 'hasApiKey'], form)
  const hasEmbeddingApiKey = Form.useWatch(['ai', 'hasEmbeddingApiKey'], form)
  const hasSlackWebhook = Form.useWatch(['notifications', 'hasSlackWebhook'], form)
  const hasWebhookUrl = Form.useWatch(['integrations', 'hasWebhookUrl'], form)

  // 加载设置
  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  // 当租户配置加载完成后，更新租户语言
  useEffect(() => {
    if (companySettingsData?.settings?.general?.language) {
      setTenantLanguage(companySettingsData.settings.general.language as SupportedLanguage)
    }
  }, [companySettingsData])

  // 合并用户设置和租户设置，更新表单
  useEffect(() => {
    // 等待用户设置加载完成
    if (isLoading || !settings) return

    // 检查租户设置是否已加载
    if (!companySettingsData) return

    // 检查租户设置是否发生变化
    const currentCompanySettings = JSON.stringify(companySettingsData.settings?.ai)
    const previousCompanySettings = JSON.stringify(previousCompanySettingsDataRef.current)

    // 如果租户设置没有变化且已经应用过，则跳过
    if (hasAppliedTenantSettingsRef.current && currentCompanySettings === previousCompanySettings) {
      console.log('租户设置未变化，跳过合并')
      return
    }

    console.log('合并设置: 用户设置已加载，租户设置:', companySettingsData?.settings?.ai)

    let mergedSettings = { ...settings }

    // 优先使用租户的嵌入模型配置
    const tenantAiSettings = companySettingsData?.settings?.ai || {}
    mergedSettings = {
      ...mergedSettings,
      ai: {
        ...mergedSettings.ai,
        embeddingModel: tenantAiSettings.embedding_model ?? defaultSettings.ai.embeddingModel,
        embeddingApiKey: tenantAiSettings.embedding_api_key ?? defaultSettings.ai.embeddingApiKey,
        hasEmbeddingApiKey: tenantAiSettings.has_embedding_api_key ?? defaultSettings.ai.hasEmbeddingApiKey,
        embeddingEndpointUrl: tenantAiSettings.embedding_endpoint_url ?? defaultSettings.ai.embeddingEndpointUrl
      }
    }
    console.log('已合并租户嵌入模型配置:', tenantAiSettings.embedding_model)

    // 更新引用
    previousCompanySettingsDataRef.current = companySettingsData?.settings?.ai
    hasAppliedTenantSettingsRef.current = true

    form.setFieldsValue(mergedSettings)
    console.log('表单值已更新:', mergedSettings.ai?.embeddingModel)
  }, [isLoading, settings, companySettingsData, form])

  // 显示错误信息
  useEffect(() => {
    if (error) {
      message.error(error)
    }
  }, [error])

  const handleSave = async () => {
    try {
      // 获取当前表单值
      const values = await form.validateFields()

      // 分离嵌入模型配置（租户级别）和其他设置（用户级别）
      const { ai, ...otherSettings } = values
      let userAISettings = {}
      let embeddingConfig = {}

      if (ai) {
        // 嵌入模型配置单独保存到租户设置
        embeddingConfig = {
          embedding_model: ai.embeddingModel,
          embedding_api_key: ai.embeddingApiKey,
          embedding_endpoint_url: ai.embeddingEndpointUrl
        }

        // 用户个人AI设置（保留 has 标记和嵌入模型字段，避免 store 数据丢失）
        userAISettings = {
          model: ai.model,
          temperature: ai.temperature,
          maxTokens: ai.maxTokens,
          enableContextWindow: ai.enableContextWindow,
          contextWindow: ai.contextWindow,
          maxResponseChars: ai.maxResponseChars,
          enableStreaming: ai.enableStreaming,
          apiKey: ai.apiKey,
          hasApiKey: ai.hasApiKey ?? settings.ai.hasApiKey,
          hasEmbeddingApiKey: ai.hasEmbeddingApiKey ?? settings.ai.hasEmbeddingApiKey,
          embeddingModel: ai.embeddingModel,
          embeddingApiKey: ai.embeddingApiKey,
          embeddingEndpointUrl: ai.embeddingEndpointUrl
        }
      }

      // 更新store中的用户设置（不包含嵌入模型配置）
      updateSettings({
        ...otherSettings,
        ai: userAISettings
      })

      // 保存用户设置到后端
      await saveSettings()

      // 保存嵌入模型配置到租户设置
      console.log('准备保存嵌入模型配置到租户设置:', embeddingConfig)

      // 过滤掉undefined和空字符串值
      const filteredConfig = Object.fromEntries(
        Object.entries(embeddingConfig).filter(([_, v]) => v !== undefined && v !== '')
      )

      if (Object.keys(filteredConfig).length > 0) {
        try {
          await updateCompanySettings.mutateAsync({
            ai: filteredConfig
          })
          console.log('嵌入模型配置已保存到租户设置')
        } catch (error) {
          console.error('保存嵌入模型配置到租户设置失败:', error)
          if (isAdmin) {
            message.warning('嵌入模型配置保存到租户设置失败，请联系管理员')
          }
          // 不阻止整个保存流程，只记录错误
        }
      } else {
        console.log('没有有效的嵌入模型配置需要保存到租户设置')
      }

      // 刷新 authStore 中的公司信息（更新 localStorage）
      await refreshUserInfo()

      // 重新获取设置，刷新 hasApiKey 等标志位
      hasAppliedTenantSettingsRef.current = false
      await fetchSettings()

      message.success(t('settings.messages.saveSuccess'))
    } catch (error) {
      console.error('保存设置失败:', error)
      // 错误信息已由store处理
    }
  }

  const handleReset = () => {
    resetSettings()
    // 获取重置后的设置（不包含嵌入模型配置或使用默认值）
    const resetValues = { ...settings }

    // 保留当前的嵌入模型配置（从租户设置或当前表单）
    const currentFormValues = form.getFieldsValue()
    if (currentFormValues.ai) {
      resetValues.ai = {
        ...resetValues.ai,
        embeddingModel: currentFormValues.ai.embeddingModel,
        embeddingApiKey: currentFormValues.ai.embeddingApiKey,
        hasEmbeddingApiKey: currentFormValues.ai.hasEmbeddingApiKey,
        embeddingEndpointUrl: currentFormValues.ai.embeddingEndpointUrl
      }
    }

    form.setFieldsValue(resetValues)
    message.info(t('settings.messages.resetToDefaults'))
  }

  const handleTenantLanguageChange = async (language: SupportedLanguage) => {
    try {
      
      // 1. 更新后端配置
      const result = await updateCompanySettings.mutateAsync({ general: { language } })
      
      // 2. 等待一小段时间确保数据库事务完成
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // 3. 刷新 authStore 中的公司信息（更新 localStorage）
      await refreshUserInfo()
      
      // 4. 立即应用语言到UI
      await changeLanguage(language)
      
      // 5. 更新本地状态
      setTenantLanguage(language)
      
      message.success(t('settings.messages.tenantLanguageUpdated'))
    } catch (error) {
      message.error(t('settings.messages.tenantLanguageUpdateFailed'))
      console.error('❌ 更新租户默认语言失败:', error)
    }
  }

  // 检查是否是管理员
  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin'

  const items: TabsProps['items'] = [
    {
      key: 'general',
      label: t('settings.tabs.general'),
      children: (
        <AppCard className="settings-section">
          <h3>{t('settings.sections.basicConfiguration')}</h3>

          {/* 租户默认语言配置（仅管理员可见） */}
          {isAdmin && (
            <>
              <Alert
                message={t('settings.sections.tenantLanguage')}
                description={t('settings.descriptions.tenantLanguage')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                label={t('settings.labels.tenantUILanguage')}
                help={t('settings.help.tenantLanguage')}
              >
                <Select
                  value={tenantLanguage}
                  onChange={handleTenantLanguageChange}
                  loading={isLoadingCompanySettings || updateCompanySettings.isPending}
                  disabled={updateCompanySettings.isPending}
                >
                  {Object.entries(LANGUAGE_DISPLAY_NAMES).map(([code, names]) => (
                    <Option key={code} value={code}>
                      {names.native} ({names.english})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Divider />
            </>
          )}

          <Form.Item
            label={t('settings.labels.timezone')}
            name={['general', 'timezone']}
          >
            <Select>
              <Option value="Asia/Shanghai">{t('settings.options.timezones.asiaShanghai')}</Option>
              <Option value="America/New_York">{t('settings.options.timezones.americaNewYork')}</Option>
              <Option value="Europe/London">{t('settings.options.timezones.europeLondon')}</Option>
            </Select>
          </Form.Item>

        </AppCard>
      ),
    },
    {
      key: 'ai',
      label: t('settings.tabs.ai'),
      children: (
        <div className="ai-settings-container">
          {/* 卡片1: AI模型配置 */}
          <AppCard className="settings-section ai-settings-card" style={{ marginBottom: 16 }}>
            <h3>{t('settings.sections.modelConfiguration')}</h3>
            <Form.Item
              label={t('settings.labels.aiModel')}
              name={['ai', 'model']}
              rules={[{ required: true, message: '请选择AI模型' }]}
            >
              <Select>
                <Option value="gpt-4">{t('settings.options.models.gpt4')}</Option>
                <Option value="gpt-3.5-turbo">{t('settings.options.models.gpt35Turbo')}</Option>
                <Option value="claude-3">{t('settings.options.models.claude3')}</Option>
                <Option value="deepseek-chat">{t('settings.options.models.deepseekChat')}</Option>
                <Option value="deepseek-reasoner">{t('settings.options.models.deepseekReasoner')}</Option>
                <Option value="local-model">Local Model</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={t('settings.labels.temperature')}
              name={['ai', 'temperature']}
            >
              <Slider
                min={0}
                max={1}
                step={0.1}
                marks={{
                  0: t('settings.options.temperatureMarks.precise'),
                  0.5: t('settings.options.temperatureMarks.balanced'),
                  1: t('settings.options.temperatureMarks.creative'),
                }}
              />
            </Form.Item>

            <Form.Item
              label={t('settings.labels.maxTokens')}
              name={['ai', 'maxTokens']}
            >
              <Slider
                min={100}
                max={4000}
                step={100}
                marks={{
                  100: '100',
                  2000: '2000',
                  4000: '4000',
                }}
              />
            </Form.Item>

            <SensitiveFieldInput
              name={['ai', 'apiKey']}
              label={t('settings.labels.apiKey')}
              placeholder={t('settings.placeholders.enterApiKey')}
              hasValue={!!hasApiKey}
              isAdmin={isAdmin}
              rules={[]}
              fieldType="apiKey"
            />

            <Divider />

            <Form.Item
              label={t('settings.labels.enableContextWindow')}
              name={['ai', 'enableContextWindow']}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            {enableContextWindow && (
              <Form.Item
                label={t('settings.labels.contextWindow') || 'Context Window (tokens)'}
                name={['ai', 'contextWindow']}
                help={t('settings.help.contextWindow') || '模型上下文窗口大小，用于控制对话历史自动压缩的触发阈值'}
              >
                <InputNumber
                  min={1024}
                  max={200000}
                  step={1024}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}

            <Form.Item
              label={t('settings.labels.maxResponseChars') || 'Max Response Characters'}
              name={['ai', 'maxResponseChars']}
              help={t('settings.help.maxResponseChars') || '最终回复的最大字符数限制，0 表示不限制。WhatsApp 等渠道建议设为 1500'}
            >
              <InputNumber
                min={0}
                max={10000}
                step={100}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              label={t('settings.labels.enableStreaming')}
              name={['ai', 'enableStreaming']}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </AppCard>

          {/* 卡片2: 嵌入模型配置 */}
          <AppCard className="settings-section ai-settings-card">
            <h3>{t('settings.sections.embeddingConfiguration')}</h3>
            <Form.Item
              label={t('settings.labels.embeddingModel')}
              name={['ai', 'embeddingModel']}
              help={knowledgeBaseStatus?.has_documents
                ? t('settings.help.embeddingModel.hasDocuments')
                : t('settings.help.embeddingModel.general')}
            >
              <Select
                disabled={knowledgeBaseStatus?.has_documents === true || isLoadingKnowledgeBaseStatus}
              >
                <Option value="local">{t('settings.options.embeddingModels.local')}</Option>
                <Option value="dashscope">{t('settings.options.embeddingModels.dashscope')}</Option>
              </Select>
            </Form.Item>

            {/* 动态显示字段 */}
            {embeddingModel === 'dashscope' && (
              <SensitiveFieldInput
                name={['ai', 'embeddingApiKey']}
                label={t('settings.labels.embeddingApiKey')}
                placeholder={t('settings.placeholders.enterEmbeddingApiKey')}
                hasValue={!!hasEmbeddingApiKey}
                isAdmin={isAdmin}
                rules={[
                  // 如果hasEmbeddingApiKey为false（未设置密码），则要求输入
                  ({ getFieldValue }: { getFieldValue: (name: string | string[]) => any }) => ({
                    validator(_: any, value: string) {
                      const hasValue = getFieldValue(['ai', 'hasEmbeddingApiKey'])
                      if (!hasValue && !value) {
                        return Promise.reject(new Error(t('settings.messages.embeddingApiKeyRequired')))
                      }
                      return Promise.resolve()
                    }
                  })
                ]}
                fieldType="apiKey"
              />
            )}

            {embeddingModel === 'local' && (
              <Form.Item
                label={t('settings.labels.embeddingEndpointUrl')}
                name={['ai', 'embeddingEndpointUrl']}
                help={t('settings.help.embeddingEndpointUrl')}
                rules={[
                  { required: true, message: t('settings.messages.embeddingEndpointUrlRequired') },
                  { type: 'url', message: t('settings.messages.embeddingEndpointUrlInvalid') }
                ]}
              >
                <Input placeholder={t('settings.placeholders.enterEmbeddingEndpointUrl')} />
              </Form.Item>
            )}
          </AppCard>
        </div>
      ),
    },
    {
      key: 'notifications',
      label: t('settings.tabs.notifications'),
      children: (
        <AppCard className="settings-section">
          <h3>{t('settings.sections.emailNotifications')}</h3>
          <Form.Item
            label={t('settings.labels.enableEmailNotifications')}
            name={['notifications', 'emailEnabled']}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={t('settings.labels.emailRecipients')}
            name={['notifications', 'emailRecipients']}
          >
            <Select mode="tags" placeholder={t('settings.placeholders.enterEmail')} />
          </Form.Item>

          <Divider />

          <h3>{t('settings.sections.slackNotifications')}</h3>
          <Form.Item
            label={t('settings.labels.enableSlackNotifications')}
            name={['notifications', 'slackEnabled']}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <SensitiveFieldInput
            name={['notifications', 'slackWebhook']}
            label={t('settings.labels.slackWebhook')}
            placeholder={t('settings.placeholders.enterSlackWebhook')}
            hasValue={!!hasSlackWebhook}
            isAdmin={isAdmin}
            rules={[]}
            inputType="text"
            fieldType="webhook"
          />

          <Divider />

          <h3>{t('settings.sections.alertConfiguration')}</h3>
          <Form.Item
            label={t('settings.labels.enableErrorAlerts')}
            name={['notifications', 'enableErrorAlerts']}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </AppCard>
      ),
    },
    {
      key: 'integrations',
      label: t('settings.tabs.integrations'),
      children: (
        <AppCard className="settings-section">
          <h3>{t('settings.sections.webhookConfiguration')}</h3>
          <Form.Item
            label={t('settings.labels.enableWebhook')}
            name={['integrations', 'enableWebhook']}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <SensitiveFieldInput
            name={['integrations', 'webhookUrl']}
            label={t('settings.labels.webhookUrl')}
            placeholder={t('settings.placeholders.enterWebhookUrl')}
            hasValue={!!hasWebhookUrl}
            isAdmin={isAdmin}
            rules={[]}
            inputType="text"
            fieldType="url"
          />

          <Divider />

          <h3>{t('settings.sections.apiConfiguration')}</h3>
          <Form.Item
            label={t('settings.labels.enableExternalApi')}
            name={['integrations', 'enableAPI']}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={t('settings.labels.apiRateLimit')}
            name={['integrations', 'apiRateLimit']}
          >
            <Slider
              min={10}
              max={1000}
              step={10}
              marks={{
                10: '10',
                100: '100',
                500: '500',
                1000: '1000',
              }}
            />
          </Form.Item>

          <Form.Item
            label={t('settings.labels.allowedOrigins')}
            name={['integrations', 'allowedOrigins']}
          >
            <Select mode="tags" placeholder={t('settings.placeholders.enterAllowedOrigins')} />
          </Form.Item>

          <Divider />

          <WhatsAppChannelConfig />
        </AppCard>
      ),
    },
    // 租户导出导入（管理员可见）
    ...(isAdmin ? [{
      key: 'tenantExport',
      label: t('settings.tabs.tenantExport'),
      children: (
        <AppCard className="settings-section">
          <TenantExportImport />
        </AppCard>
      ),
    }] : []),
  ]

  return (
    <div className="system-settings">
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            console.log('表单值变化:', changedValues, allValues)

            // 更新store中的所有设置，包括嵌入模型配置（嵌入模型配置仅保存在租户设置中）
            const { ai, ...otherSettings } = allValues
            let userAISettings = {}

            if (ai) {
              userAISettings = {
                model: ai.model,
                temperature: ai.temperature,
                maxTokens: ai.maxTokens,
                enableContextWindow: ai.enableContextWindow,
                contextWindow: ai.contextWindow,
                maxResponseChars: ai.maxResponseChars,
                enableStreaming: ai.enableStreaming,
                apiKey: ai.apiKey,
                hasApiKey: ai.hasApiKey ?? settings.ai.hasApiKey,
                hasEmbeddingApiKey: ai.hasEmbeddingApiKey ?? settings.ai.hasEmbeddingApiKey,
                embeddingModel: ai.embeddingModel,
                embeddingApiKey: ai.embeddingApiKey,
                embeddingEndpointUrl: ai.embeddingEndpointUrl
              }
            }

            updateSettings({
              ...otherSettings,
              ai: userAISettings,
            })
          }}
          onFinish={handleSave}
          size="middle"
        >
          {/* 隐藏字段：让 Form.useWatch 能监听 has 标记状态 */}
          <Form.Item name={['ai', 'hasApiKey']} hidden><Input /></Form.Item>
          <Form.Item name={['ai', 'hasEmbeddingApiKey']} hidden><Input /></Form.Item>
          <Form.Item name={['notifications', 'hasSlackWebhook']} hidden><Input /></Form.Item>
          <Form.Item name={['integrations', 'hasWebhookUrl']} hidden><Input /></Form.Item>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            tabBarExtraContent={
              <div className="settings-actions">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  style={{ marginRight: 8 }}
                >
                  {t('settings.buttons.reset')}
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={isSaving}
                >
                  {t('settings.buttons.saveSettings')}
                </Button>
              </div>
            }
            items={items}
          />
        </Form>
    </div>
  )
}

export default SystemSettings